import {AfterViewInit, Component, Input} from '@angular/core';
import {
  ClosedSalesModel,
  GrandTotalsModel,
  PayoutSummaryModel,
  RetailerTransactionStatementsModel
} from '@app/pages/admin-panel/retailer-transaction-statement/retailer-transaction-statements.model';
import {CurrencyPipe, NgForOf, NgIf} from '@angular/common';
import {displayShortDate} from '@ui-kit/utils/static/date-format-utils';


type PageContent =
  { type: 'closedSales'; data: ClosedSalesModel[] } |
  { type: 'payout'; data: PayoutSummaryModel[] } |
  { type: 'grandTotal'; data: GrandTotalsModel[] };


@Component({
  standalone: true,
  templateUrl: './transaction-statement.component.html',
  imports: [
    NgForOf,
    NgIf,
    CurrencyPipe
  ],
  styleUrls: ['./transaction-statement.component.scss'],
  selector: 'app-transaction-summary-invoice',
})
export class TransactionStatementComponent implements AfterViewInit {
  @Input()
  summary!: RetailerTransactionStatementsModel;

  @Input()
  currency!: string;

  @Input()
  dateIssued!: string;
  imageSrc: string | null = null;
  paginatedContent: PageContent[][] = [];
  sameTypeRowLimit = 20; // Limit for same-type content
  mixedTypeRowLimit = 10; // Limit for mixed content
  defaultRowsPerPage = 20; // Default rows per page
  protected readonly displayShortDate = displayShortDate;

  constructor() {
    this.imageSrc = '../../../../assets/brand/brand-icon.svg';
  }


  ngAfterViewInit() {
    this.paginateTables();
  }

  // Function to paginate invoices and payouts data
  paginateTables() {
    let currentPage: PageContent[] = [];
    let currentRowCount = 0;

    const calculateRowLimit = (type: 'closedSales' | 'payout' | 'grandTotal') => {
      const typesOnPage = currentPage.map((section) => section.type);
      if (typesOnPage.length > 1 || (typesOnPage.length === 1 && !typesOnPage.includes(type))) {
        return this.mixedTypeRowLimit; // Mixed content row limit
      } else if (typesOnPage.length === 1) {
        return this.sameTypeRowLimit; // Single-type content row limit
      }
      return this.defaultRowsPerPage; // Default limit for an empty page
    };

    const addDataToPage = (data: any[], type: 'closedSales' | 'payout', rowsRequired: number, forceNewPage = false) => {
      for (const item of data) {
        const rowLimit = calculateRowLimit(type);

        // Start a new page if rows exceed limit or if a new page is forced
        if (currentRowCount + rowsRequired > rowLimit || forceNewPage) {
          this.paginatedContent.push([...currentPage]); // Save current page
          currentPage = [];
          currentRowCount = 0;
          forceNewPage = false; // Reset flag after forcing a new page
        }

        // Add data section to the current page if not already added
        if (!currentPage.find((section) => section.type === type)) {
          currentPage.push({type, data: []});
        }
        const section = currentPage.find((section) => section.type === type)!;
        section.data.push(item);
        currentRowCount++;
      }
    };

    // Add closed sales data
    addDataToPage(this.summary.closedSales, 'closedSales', 1);

    // Add payout data
    addDataToPage(this.summary.payoutSummary, 'payout', 1);

    // Add grand totals if there's space left on the current page
    const rowLimitForGrandTotal = 15;
    if (currentRowCount <= rowLimitForGrandTotal) {
      currentPage.push({type: 'grandTotal', data: [this.summary.grandTotals]});
      this.paginatedContent.push(currentPage); // Save the final page
    } else {
      // Otherwise, start a new page for the grand total
      this.paginatedContent.push(currentPage); // Save the current page
      this.paginatedContent.push([{type: 'grandTotal', data: [this.summary.grandTotals]}]); // Save the grand total on a new page
    }
  }

  shouldShowClosedSalesTotals(pageIndex: number): boolean {

    let currentPage: PageContent[] = this.paginatedContent[pageIndex];

    let sections: ("closedSales" | "payout" | "grandTotal")[] = currentPage.map(page => page.type);

    // there are mixed sections that means on this page the closed sales finish
    if (sections.length > 1) {
      return true;
    }

  //   check if the current page is the last page of content at all
    if (pageIndex == this.paginatedContent.length - 1) {
      return true;
    }

  //   check if on the next page the first section is payouts or grandtotals
    let nextPageSections: ("closedSales" | "payout" | "grandTotal")[] = this.paginatedContent[pageIndex + 1].map(section => section.type);
    if (nextPageSections.includes('closedSales')) {
      return false;
    }

    return true;


  }

  shouldShowPayoutTotals(pageIndex: number): boolean {
    let currentPage: PageContent[] = this.paginatedContent[pageIndex];

    let sections: ("closedSales" | "payout" | "grandTotal")[] = currentPage.map(page => page.type);

  //   if on the current page that I am on grand total is there, show totals
    if (sections.includes('grandTotal')) {
      return true;
    }

    //   check if the current page is the last page of content at all
    if (pageIndex == this.paginatedContent.length - 1) {
      return true;
    }

  //   check if the next page includes grand total, if it does don't show
    let nextPageSections: ("closedSales" | "payout" | "grandTotal")[] = this.paginatedContent[pageIndex + 1].map(section => section.type);
    if (nextPageSections.includes('grandTotal')) {
      return false;
    }

    return true;
  }

  makeNegative(totalSettled: number): number {
    if (totalSettled === 0) {
      return totalSettled;
    }
    if (totalSettled > 0) {
      return totalSettled * -1;
    }
    return totalSettled;
  }
}
